import {
  Box,
  Container,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import { Link } from "react-router-dom";
import { IoMdPhonePortrait } from "react-icons/io";
import { FaTabletAlt, FaApple, FaChromecast } from "react-icons/fa";
import { HiMiniComputerDesktop } from "react-icons/hi2";
import { BsFillSpeakerFill } from "react-icons/bs";
import { SiSonos, SiAmazonalexa, SiTunein, SiAndroidauto } from "react-icons/si";
import { FiAirplay } from "react-icons/fi";
import Heos from "Assets/heos.png";

function Device(props: {
  icon?: React.ElementType;
  image?: string;
  title: string;
  body: string | JSX.Element;
  height?: string;
  size?: number;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Grid
      item
      xs={6}
      md={3}
      sx={{ px: 1, pt: isMobile ? 2 : 0, mt: isMobile ? 3 : 5 }}
    >
      {props?.image && (
        <img
          src={props.image}
          height={props?.height ? props.height : "50px"}
          alt="icon"
          style={{ marginTop: isMobile ? 3 : 25 }}
        />
      )}

      {props?.icon && (
        <props.icon size={props?.size ? props.size : 70} color="#ffffff" />
      )}
      {props?.title && (
        <Box style={{ marginTop: 4 }}>
          <Typography
            sx={{
              fontFamily: "futura-pt",
              fontWeight: 450,
              fontSize: "1.5em",
            }}
          >
            {props.title}
          </Typography>
        </Box>
      )}
      {props?.body && (
        <Typography
          sx={{
            fontFamily: "futura-pt",
            fontWeight: 400,
            fontSize: "1.5em",
            color: "#b0b0b0",
          }}
        >
          {props.body}
        </Typography>
      )}
    </Grid>
  );
}

interface DevicesShowcaseProps {
  /**
   * When provided, the "Try it free to unlock device setup!" call-to-action is
   * rendered and invokes this handler on click (e.g. opening the Auth0 lock).
   * Omit it in contexts where the CTA doesn't apply (such as the donation
   * checkout modal).
   */
  onTryFree?: () => void;
  /**
   * Style overrides merged onto the outer card. Used to drop the page-level
   * fixed height/margins when the showcase is embedded (e.g. inside a modal).
   */
  sx?: SxProps<Theme>;
}

const DevicesShowcase = ({ onTryFree, sx }: DevicesShowcaseProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isSmallDesktop = useMediaQuery(theme.breakpoints.down("lg"));

  return (
    <Grid
      container
      sx={[
        {
          height: isSmallMobile
            ? "1375px"
            : isMobile
            ? "1275px"
            : isSmallDesktop
            ? "1075px"
            : "940px",
          maxWidth: "lg",
          margin: "220px auto 0",
          overflow: "visible",
          zIndex: 5,
          isolation: "isolate",
          background: `#3C3C3C`,
          borderRadius: "18px",
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Grid
        item
        sm={12}
        sx={{
          textAlign: "center",
          overflow: "visible",
        }}
      >
        <Container
          sx={{
            spacing: 0,
            paddingTop: "50px",
            zIndex: 5,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box sx={{ mt: 1, mx: isMobile ? "20px" : "240px" }}>
            <Typography
              sx={{
                fontFamily: "futura-pt",
                fontWeight: 450,
                fontSize: isMobile ? "2em" : "3.5em",
              }}
            >
              Listen To The Jazz Groove On All Your Devices
            </Typography>
          </Box>
          <Grid
            container
            sx={{
              pt: 2,
              maxWidth: "1000px",
              margin: "auto",
              mb: 4,
            }}
          >
            <Device
              icon={IoMdPhonePortrait}
              title={"Smartphones"}
              body={"iPhone & Android"}
            />
            <Device
              icon={HiMiniComputerDesktop}
              title={"Desktops"}
              body={"PC & Mac"}
            />
            <Device
              icon={FaTabletAlt}
              title={"Tablets"}
              body={"iOS & Android"}
            />
            <Device
              icon={BsFillSpeakerFill}
              title={"Speakers"}
              body={"TV & Audio"}
            />
            <Device icon={SiSonos} title={""} body={""} size={120} />
            <Device icon={SiAmazonalexa} title={"Amazon Alexa"} body={""} />
            <Device icon={SiTunein} title={"TuneIn"} body={""} />
            <Device image={Heos} title={""} height="50px" body={""} />
            <Device icon={SiAndroidauto} title={"Android Auto"} body={""} />
            <Device icon={FaApple} title={"Apple CarPlay"} body={""} />
            <Device icon={FaChromecast} title={"Google Cast"} body={""} />
            <Device icon={FiAirplay} title={"Apple AirPlay"} body={""} />
          </Grid>
          <Grid
            container
            sx={{
              pt: 2,
              maxWidth: "400px",
              margin: "0 auto",
              mb: 4,
              textAlign: "center",
            }}
          >
            <Typography
              sx={{
                fontFamily: "futura-pt",
                fontWeight: 400,
                fontSize: isMobile ? "1em" : "1.5em",
                whiteSpace: "pre-line",
                margin: "0 auto",
              }}
            >
              Plus Many Other Audio Devices & Apps
              {onTryFree && (
                <>
                  {"\n"}
                  <Link
                    to={""}
                    onClick={(e) => {
                      e.preventDefault();
                      onTryFree();
                    }}
                    className="tryitfree"
                  >
                    Try it free to unlock device setup!
                  </Link>
                </>
              )}
            </Typography>
          </Grid>
        </Container>
      </Grid>
    </Grid>
  );
};

export default DevicesShowcase;
